import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, KV, Stat, StatGrid } from '../components/Section';

export function InfraPage() {
  const scan = useScan();
  const dns = scan.modules.dns;
  const tech = scan.modules.techstack;
  const web = scan.modules.web;
  const infra = scan.modules.infra;

  const cdnHosts = tech?.technologies
    .filter(t => t.category === 'CDN' || t.category === 'Hosting')
    .map(t => t.name)
    .join(', ');

  return (
    <>
      <PageHeader
        title="Infrastructure"
        subtitle="Where the domain lives — network owner (ASN), reverse DNS, hosting, and edge"
      />

      {infra && infra.cdn_fronted && (
        <Section accent="slate">
          <Banner tone="info" title="Requests arrive at an edge / CDN, not the origin">
            The IPs below resolve to {infra.providers.join(', ') || 'a CDN'}. The real
            origin server sits behind that edge and is not directly visible from the
            public internet.
          </Banner>
        </Section>
      )}

      {/* Network intelligence from the new infra analyzer (PTR + Team Cymru ASN). */}
      {infra && infra.hosts.length > 0 ? (
        <Section title="Network owner &amp; hosting" accent="blue"
          status={infra.providers.length ? { label: infra.providers[0], tone: 'info' } : undefined}>
          <StatGrid cols={4}>
            <Stat label="Hosts resolved" value={infra.hosts.length} />
            <Stat label="Distinct ASNs" value={infra.asns.length || '—'} />
            <Stat label="Providers" value={infra.providers.length ? infra.providers.join(', ') : '—'} tone="info" />
            <Stat label="Edge / CDN" value={infra.cdn_fronted ? 'Yes' : 'No'} tone={infra.cdn_fronted ? 'info' : 'mute'} />
          </StatGrid>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-line text-[11px] uppercase tracking-wider text-ink-3">
                  <th className="py-2 pr-3 font-semibold">IP</th>
                  <th className="py-2 pr-3 font-semibold">Reverse DNS (PTR)</th>
                  <th className="py-2 pr-3 font-semibold">ASN</th>
                  <th className="py-2 pr-3 font-semibold">Network</th>
                  <th className="py-2 pr-3 font-semibold">Prefix</th>
                  <th className="py-2 pr-3 font-semibold">RIR</th>
                  <th className="py-2 pr-3 font-semibold">Country</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {infra.hosts.map(h => (
                  <tr key={h.ip} className="align-top">
                    <td className="py-2 pr-3 font-mono text-[13px] text-ink">{h.ip}</td>
                    <td className="py-2 pr-3 font-mono text-[12px] text-ink-2 break-all">{h.ptr ?? '—'}</td>
                    <td className="py-2 pr-3 font-mono text-[13px] text-ink-2">{h.asn ? `AS${h.asn}` : '—'}</td>
                    <td className="py-2 pr-3 text-ink-2">{h.provider ?? h.as_name ?? '—'}</td>
                    <td className="py-2 pr-3 font-mono text-[12px] text-ink-2">{h.prefix ?? '—'}</td>
                    <td className="py-2 pr-3 uppercase text-ink-2">{h.registry ?? '—'}</td>
                    <td className="py-2 pr-3 text-ink-2">{h.country ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-ink-3">
            ASN / prefix / country from Team Cymru IP-to-ASN; reverse DNS via DoH. Descriptive only — not part of the security score.
          </p>
        </Section>
      ) : (
        <Section title="Network owner &amp; hosting" accent="blue">
          <Banner tone="info" title="No network intelligence resolved">
            We couldn't resolve ASN / reverse-DNS for this domain's IPs (no A/AAAA records, or the lookups were unavailable).
          </Banner>
        </Section>
      )}

      <Section title="DNS records" accent="blue">
        <div className="grid gap-4 md:grid-cols-2">
          <KV k="A Records (IPv4)" v={dns?.records.a.join(', ') || '—'} />
          <KV k="AAAA Records (IPv6)" v={dns?.records.aaaa.join(', ') || '—'} />
          <KV k="Nameservers" v={dns?.records.ns.join(', ') || '—'} />
          <KV k="Mail Exchanges" v={
            dns?.records.mx.length
              ? dns.records.mx.map(x => `${x.preference} ${x.exchange}`).join(' • ')
              : '—'
          } />
        </div>
        {dns?.nameservers && (
          <div className="mt-4 border-t border-line pt-4">
            <KV
              k={`DNS operators (${dns.nameservers.count} NS)`}
              v={
                <span className={dns.nameservers.single_operator ? 'text-amber-700' : 'text-ink'}>
                  {dns.nameservers.providers.join(', ')}
                  {dns.nameservers.single_operator && ' — single operator (no cross-provider redundancy)'}
                </span>
              }
            />
          </div>
        )}
      </Section>

      <Section title="Edge / server fingerprint" accent="blue">
        <div className="grid gap-4 md:grid-cols-2">
          <KV k="Server Header" v={web?.headers.server ?? '—'} />
          <KV k="CDN / Hosting (fingerprinted)" v={cdnHosts || '—'} />
        </div>
      </Section>
    </>
  );
}
